import { asRawImageRefs, resolveStorageUrls } from "@/lib/storage/public-url";
import type { RawImageRef } from "@/lib/storage/public-url";

/**
 * Canonical read funnel for `listings.image_urls` when the column arrives as an
 * UNKNOWN shape — a real array, a JSON-encoded string, or nullish. That is the
 * case everywhere the column travels inside a chat-message payload / realtime
 * broadcast, where it is never re-parsed by a typed boundary.
 *
 * TWO exits, one parse:
 *  - {@link parseListingImageUrls} — RESOLVED absolute `string[]`, for values
 *    headed to an `<img>` / `next/image` / `fetch` sink. The default.
 *  - {@link parseListingImageRefs} — RAW `RawImageRef[]`, for values that get
 *    written back to a DB column or snapshotted into a checkout payload
 *    (display ≠ persistence, handoff §9). Resolving those at the producer
 *    would push absolute URLs back into the DB and undo phase C.
 *
 * Resolving MUST happen at the parse step for the display exit, not at the JSX:
 * the parsed array is aliased downstream (`images` → `slides` → `hero`,
 * `galleryImages`, checkout payloads), and only some of those aliases pass
 * through `toSupabasePublicImageThumbUrl`. A bare path reaching `next/image`
 * throws `Failed to construct 'URL'` — a live crash on the chat listing sheet
 * (2026-07-27) caused by a copy-pasted local parser that skipped the resolve.
 *
 * Local forks were replaced by this module; do not re-fork it — the whole point
 * is that there is one place where the resolve cannot be forgotten. New forks
 * are blocked by `tests/unit/fitness-no-local-image-parsers.test.ts`.
 *
 * Total and idempotent: absolute URLs, `blob:`/`data:` previews and
 * `/local.svg` assets pass through unchanged, so it is safe to apply again
 * downstream.
 *
 * @see lib/storage/url.ts  behaviour table
 * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L3, §9
 */
export function parseListingImageUrls(raw: unknown): string[] {
  return resolveStorageUrls(parseImageRefStrings(raw));
}

/** Un-resolved (persistence-safe) exit — see the module header. */
export function parseListingImageRefs(raw: unknown): RawImageRef[] {
  return asRawImageRefs(parseImageRefStrings(raw));
}

/** Shape normalisation only — no resolve, no brand. */
function parseImageRefStrings(raw: unknown): string[] {
  if (!raw) return [];

  const candidates = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? tryJsonArray(raw)
      : [];

  return candidates.filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );
}

function tryJsonArray(raw: string): unknown[] {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
